import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, ImageUploadFile } from '@/app/types';

interface UseImagesSlotsProps {
  slotCounts: number;
  formType: 'img2video' | 'character2video' | 'scene';
  updateImagePrompt: (idx: number, imagePrompt: FormImagePrompt | null) => void;
}

export const useImageSlots = ({ slotCounts = 1, formType, updateImagePrompt }: UseImagesSlotsProps) => {
  const { imagePrompts, updateFormParams, setFormParams, updateTemplateFormParams } = useStore((state) => ({
    imagePrompts: state[`${formType}FormParams`].imagePrompts,
    updateFormParams: state.updateFormParams,
    setFormParams: state.setFormParams,
    updateTemplateFormParams: state.updateTemplateFormParams,
  }));
  const store = useStoreApi();

  // 仅统计当前可见槽位内的已占用数量，避免当模板切换到更少槽位时出现负数
  const visibleFilledCount = imagePrompts.slice(0, slotCounts).filter((prompt) => prompt).length;
  const emptySlotsCount = Math.max(0, slotCounts - visibleFilledCount);

  const findAvailableSlot = (idx: number) => {
    const imagePrompts = store.getState()[`${formType}FormParams`].imagePrompts;

    for (let i = 0; i < slotCounts; i++) {
      // 从当前位置 idx 开始，循环查找空位
      const currentIdx = (idx + i) % slotCounts;

      // 找到空位，直接返回
      if (!imagePrompts[currentIdx]) {
        return currentIdx;
      }
    }

    return -1;
  };

  const handleChangeFiles = (
    idx: number,
    uploadFile: ImageUploadFile,
    onUploadSuccess?: (targetIdx: number) => Promise<void> | void,
  ) => {
    const imagePrompts = store.getState()[`${formType}FormParams`].imagePrompts;
    const inspirationImageUrl = store.getState()[`${formType}FormParams`].inspirationImageUrl;

    if (uploadFile.status === 'removed') {
      const deletedImagePrompt = imagePrompts[idx];

      // 如果删除的图片是 Apply 的灵感图，清空提交任务的灵感参数
      if (deletedImagePrompt?.prompt?.sourceImage === inspirationImageUrl) {
        setFormParams(formType, {
          inspirationId: undefined,
          inspirationFrom: undefined,
          inspirationImageUrl: undefined,
        });
      }
      updateImagePrompt(idx, null);
      return;
    }

    // 开始上传，寻找目标槽位
    if (uploadFile.status === 'uploading') {
      // 第一个文件直接更新到当前槽位，其他文件则循环查找空位
      const isFirstFile = !uploadFile.batchIndex || uploadFile.batchIndex === 0;
      const targetIdx = isFirstFile ? idx : findAvailableSlot(idx);
      if (targetIdx === -1) {
        return;
      }
      updateImagePrompt(targetIdx, {
        id: uploadFile.id,
        status: 'uploading',
        prompt: null,
      });
      return;
    }

    // 如果上传失败，移除
    if (uploadFile.status === 'error') {
      if (formType === 'scene') {
        updateTemplateFormParams(
          'imagePrompts',
          imagePrompts.map((prompt) => (prompt?.id === uploadFile.id ? null : prompt)),
        );
      } else {
        updateFormParams(
          formType,
          'imagePrompts',
          imagePrompts.map((prompt) => (prompt?.id === uploadFile.id ? null : prompt)),
        );
      }
      return;
    }

    // 上传成功，更新图片到对应 id 的槽位
    if (onUploadSuccess) {
      const targetIdx = imagePrompts.findIndex((prompt) => prompt?.id === uploadFile.id);
      if (targetIdx === -1) {
        return;
      }
      onUploadSuccess(targetIdx); // 交给回调去决定如何更新 imagePrompts
    }
  };

  const handleSwapImages = (sourceIdx: number, targetIdx: number) => {
    const draft = [...store.getState()[`${formType}FormParams`].imagePrompts];
    [draft[sourceIdx], draft[targetIdx]] = [draft[targetIdx], draft[sourceIdx]];
    if (formType === 'scene') {
      updateTemplateFormParams('imagePrompts', draft);
    } else {
      updateFormParams(formType, 'imagePrompts', draft);
    }
  };

  return {
    emptySlotsCount,
    handleChangeFiles,
    handleSwapImages,
  };
};
